import React, { useState, useRef, useContext } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Color, Raycaster, Group, Box3 } from 'three';

import { animate } from 'framer-motion';

// import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
// import { useTranslation } from 'next-i18next';

import ChartIntroLabel from '../ChartIntroLabel/ChartIntroLabel';

import {
  createBaseScene,
  fitCameraToCenteredObject,
  alignHtmlElPositionWithWorldSpacePoint,
} from '../../threejs/utils/sceneHelpers';
import { createPhongGradientMaterial } from '../../threejs/utils/gradientMaterialHelpers';
// import {
//   createRenderPipeline,
//   createRenderPipelineWithBloom,
// } from '../../threejs/utils/createRenderPipeline';
import { createVerticalBars, scaleBarY } from '../../threejs/utils/barHelpers';

import { throttle } from 'throttle-debounce';

import useMultiplexedThreeJs from '../../hooks/useMultiplexedThreeJs';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useMediaQuery from '../../hooks/useMediaQuery';

import { AnimationsActiveContext } from '../../containers/BasePage';

import {
  DEFAULT_CHART_PADDING,
  DEFAULT_ENV_MAP_URL,
  THEME_ORANGE_PINK,
  THEME_BLUE_PURPLE,
  LAYOUT_SIMPLE,
  LAYOUT_FANCY,
  MIN_BAR_HEIGHT,
  MAX_BAR_HEIGHT,
} from '../../threejs/constants';

const orangePinkGradientColors = {
  gradientColorBottom: new Color(0xf81c6f).multiplyScalar(1.7),
  gradientColorMiddle: new Color(0xf81c6f).multiplyScalar(1.7),
  gradientColorTop: new Color(0xff5749).multiplyScalar(1.7),
};

const bluePurpleGradientColors = {
  gradientColorBottom: new Color(0x5aa0f8).multiplyScalar(2),
  gradientColorMiddle: new Color(0x733da1).multiplyScalar(2),
  gradientColorTop: new Color(0xd7336c).multiplyScalar(2),
};

import s from './BarChart3d.module.scss';

const BarChart3d = (props) => {
  const {
    title,
    description,
    infoTitle,
    infoText,
    infoLink,
    plotData,
    theme,
    textTheme,
    layout,
    // envMapUrl,

    minBarHeight,
    maxBarHeight,

    minHorizontalWidth,
    chartPadding,
  } = props;

  const { animationsActive } = useContext(AnimationsActiveContext) || {};

  const [envMap, setEnvMap] = useState(null);
  const [revealTransitionComplete, setRevealTransitionComplete] =
    useState(false);

  const labelRefs = useRef([]);
  const valueLabelRefs = useRef([]);

  const [inViewRef, isVisible] = useIntersectionObserver(0.3, undefined, true);
  const mediumMq = useMediaQuery(`(min-width: 768px)`);

  const { onThreeJsCanvasReady, handleCanvasRef, canvas, renderer } =
    useMultiplexedThreeJs({ debug: false });

  const hasNegativeValues =
    plotData?.items.filter((i) => i.value < 0).length > 0;

  // useEffect(() => {
  //   if(!renderer) {
  //     return;
  //   }

  //   new RGBELoader().load(envMapUrl, (texture) => {
  //     const gen = new PMREMGenerator(renderer);
  //     let em = gen.fromEquirectangular(texture).texture;

  //     setEnvMap(em);

  //     texture.dispose();
  //     gen.dispose();

  //   });
  // }, [renderer]);

  onThreeJsCanvasReady(
    ({ renderer, renderMultiplexedThreeJsScene }) => {
      if (!plotData?.items) {
        return;
      }

      const aspect = canvas.offsetWidth / canvas.offsetHeight;

      const gradientColors =
        theme === THEME_ORANGE_PINK
          ? orangePinkGradientColors
          : bluePurpleGradientColors;

      const { camera, scene } = createBaseScene({
        aspect,
        lightIntensity: 30,
      });

      // const { renderWithPipeline, updatePipeline } =
      //   createRenderPipelineWithBloom(canvas, renderer, scene, camera, {
      //     bloomThreshold: 0.44,
      //     bloomStrength: 0.2,
      //   });

      // const { renderWithPipeline, updatePipeline } =
      //   createRenderPipeline(canvas, renderer, scene, camera, {
      // });

      const { glassMaterial, customGlassUniforms } =
        createPhongGradientMaterial({
          gradientColors,
          envMap,
          pointerLightIntensity: 0.8,
          // dynamic noise fn
          // noiseAmount: renderer.getPixelRatio() < 2 ? 0.05 : 0.1,
          // noise texture
          noiseAmount: 0.03,
        });

      const {
        glassMaterial: flippedGlassMaterial,
        customGlassUniforms: customFlippedGlassUniforms,
      } = createPhongGradientMaterial({
        gradientColors: {
          gradientColorBottom: gradientColors.gradientColorTop,
          gradientColorMiddle: gradientColors.gradientColorMiddle,
          gradientColorTop: gradientColors.gradientColorBottom,
        },
        envMap,
        pointerLightIntensity: 0.8,
        // dynamic noise fn
        // noiseAmount: renderer.getPixelRatio() < 2 ? 0.05 : 0.1,
        // noise texture
        noiseAmount: 0.03,
      });

      // init bars

      let {
        bars,
        currMaxBarHeight,
        currNegMaxBarHeight,
        barsHaveNegativeValues,
      } = createVerticalBars({
        plotData,
        material: glassMaterial,
        flippedMaterial: flippedGlassMaterial,
        minBarHeight,
        maxBarHeight,
        layout,
      });

      const barsGroup = new Group();
      bars.forEach((b) => {
        barsGroup.add(b.mesh);
      });

      scene.add(barsGroup);

      const boundingBox = new Box3();
      boundingBox.setFromObject(barsGroup);

      if (barsHaveNegativeValues) {
        const newMinBounds = boundingBox.min.clone();
        const newMaxBounds = boundingBox.max.clone();

        newMinBounds.y = boundingBox.min.y + currNegMaxBarHeight;
        newMaxBounds.y = currMaxBarHeight * 0.5;

        boundingBox.set(newMinBounds, newMaxBounds);
      }

      const cameraMinZ = 60;
      fitCameraToCenteredObject(camera, boundingBox, chartPadding, cameraMinZ);

      if (barsHaveNegativeValues) {
        barsGroup.position.y -= currNegMaxBarHeight * 0.5;
      }

      const initialBarScale = !revealTransitionComplete ? 0.001 : 1;

      bars.forEach((b) => {
        scaleBarY(b, initialBarScale, currMaxBarHeight);
      });

      // get mouse position relative to canvas

      let mousePointer = {
        rawX: -1,
        rawY: -1,
        x: -1,
        y: -1,
        ndcX: -1,
        ndcY: -1,
        directionX: 0,
      };

      let prevMousePointerPosition = {
        ...mousePointer,
      };

      const onPointerMove = throttle(25, (e) => {
        const bounds = canvas.getBoundingClientRect();

        const rawX = e.clientX - bounds.left;
        const rawY = e.clientY - bounds.top;

        const x = rawX / bounds.width;
        const y = 1 - rawY / bounds.height;

        const newPosition = {
          // relative canvas coords in pixels in range [0, max canvas width/height in pixels]
          rawX,
          rawY,
          // normalized relative canvas coords in range [0, 1]
          x,
          y,
          // normalized relative canvas coords in range [-1, 1]
          ndcX: x * 2 - 1,
          ndcY: (y - 1) * 2 + 1,
          // horizontal movement direction
          directionX: Math.sign(rawX - prevMousePointerPosition.rawX),
        };

        mousePointer = newPosition;
        prevMousePointerPosition = {
          ...mousePointer,
        };
      });

      window.addEventListener('pointermove', onPointerMove);

      const raycaster = new Raycaster();
      let prevBarIntersects = [];

      const getBarsIntersectedByPointer = () => {
        raycaster.setFromCamera(
          {
            x: mousePointer.ndcX,
            y: mousePointer.ndcY,
          },
          camera
        );

        const intersects = raycaster.intersectObjects(scene.children, true);
        const barIntersects =
          intersects.filter((o) => o.object.name.indexOf('bar-') > -1) || [];

        // used for pointer/mouse out detection
        const origBarIntersects = [...barIntersects];
        const currentBarIntersects = barIntersects.filter(
          (hb) =>
            prevBarIntersects.filter((p) => p.object.uuid === hb.object.uuid)
              .length === 0
        );

        prevBarIntersects = [...origBarIntersects];

        return currentBarIntersects;
      };

      // trigger reveal animation for bars

      if (isVisible && !revealTransitionComplete && animationsActive) {
        bars.forEach((bar) => {
          animate(0, 1, {
            duration: 1,
            onUpdate: (p) => {
              bar.mesh.rotation.y = p * Math.PI * 2;
              scaleBarY(bar, p, currMaxBarHeight);
            },
            onComplete: () => {
              setRevealTransitionComplete(true);
            },
          });
        });
      } else if (isVisible && !revealTransitionComplete && !animationsActive) {
        setRevealTransitionComplete(true);
      }

      // main render loop
      renderMultiplexedThreeJsScene(
        (time, resolution, renderer, scissorOffsets) => {
          // update stuff
          camera.aspect = resolution.width / resolution.height;
          camera.updateProjectionMatrix();

          // updatePipeline(resolution);

          // NOTE!!!!!!
          // If using a bloom pipeline, this should be set to:
          // customGlassUniforms.uScissorOffsets.value = [0, 0];
          customFlippedGlassUniforms.uScissorOffsets.value =
            customGlassUniforms.uScissorOffsets.value = scissorOffsets;

          customFlippedGlassUniforms.uResolution.value =
            customGlassUniforms.uResolution.value = [
              resolution.width,
              resolution.height,
            ];
          customFlippedGlassUniforms.uTime.value =
            customGlassUniforms.uTime.value = time;
          customFlippedGlassUniforms.uMousePointer.value =
            customGlassUniforms.uMousePointer.value = [
              mousePointer.x,
              mousePointer.y,
            ];
          customFlippedGlassUniforms.uPixelRatio.value =
            customGlassUniforms.uPixelRatio.value = renderer.getPixelRatio();

          // rotate bars on pointer hover
          getBarsIntersectedByPointer().forEach((bar) => {
            const mouseXDiff = Math.abs(
              bar.object.userData?.prevMousePointer?.rawX - mousePointer.rawX
            );
            const mouseYDiff = Math.abs(
              bar.object.userData?.prevMousePointer?.rawY - mousePointer.rawY
            );

            // bail if mouse hasn't moved
            // this prevents endless rotation triggered when hovering bar edges etc.
            if (mouseXDiff <= 1 && mouseYDiff <= 1) {
              return;
            }

            animate(0, Math.PI * mousePointer.directionX, {
              type: 'spring',
              stiffness: 150,
              velocity: 8,
              mass: 0.7,
              onPlay: () => {
                bar.object.userData.prevMousePointer = { ...mousePointer };
              },
              onUpdate: (r) => {
                bar.object.rotation.y = r;
              },
            });
          });

          // finally, render all the stuff
          // renderWithPipeline();
          renderer.render(scene, camera);

          // ...and handle alignment of html labels
          labelRefs.current.forEach((label, i) => {
            const bottomBarP = bars[i].bottomPosition.clone();

            alignHtmlElPositionWithWorldSpacePoint({
              camera,
              resolution,
              worldSpacePosition: bottomBarP,
              element: label.element,
              alignToTopOfPoint: bars[i].value < 0,
            });
          });

          valueLabelRefs.current.forEach((label, i) => {
            const topBarP = bars[i].topPosition.clone();

            alignHtmlElPositionWithWorldSpacePoint({
              camera,
              resolution,
              worldSpacePosition: topBarP,
              element: label.element,
              alignToTopOfPoint: bars[i].value < 0 ? false : true,
            });
          });
        }
      );

      return () => {
        window.removeEventListener('pointermove', onPointerMove);
      };
    },
    [
      theme,
      envMap,
      labelRefs,
      valueLabelRefs,
      isVisible,
      revealTransitionComplete,
      mediumMq,
    ]
  );

  const rootCls = classNames(s.Root, {
    [s.HasMinWidth]: !!minHorizontalWidth,
    [s.HasNegativeValues]: hasNegativeValues,
    [s.LayoutFancy]: layout === LAYOUT_FANCY,
    [s.TextColorDark]: textTheme === 'dark',
  });

  return (
    <div ref={inViewRef} className={rootCls}>
      <div className="grid">
        <div className={s.TitleContainer}>
          {title && (
            <ChartIntroLabel
              title={title}
              infoTitle={infoTitle}
              infoText={infoText}
              infoLink={infoLink}
              description={description}
              theme={textTheme}
            />
          )}
        </div>
      </div>
      <div className={s.Container}>
        <div
          ref={handleCanvasRef}
          className={s.Canvas}
          style={
            minHorizontalWidth
              ? {
                  minWidth: `${minHorizontalWidth}px`,
                }
              : {}
          }>
          <div className={s.Labels}>
            {plotData?.items.map((v, i) => {
              const innerValueLabelWrapCls = classNames(s.InnerValueLabelWrap, {
                [s.Visible]: isVisible,
                [s.IsNegativeLabel]: v.value < 0,
              });

              const innerLabelWrapCls = classNames(s.InnerLabelWrap, {
                [s.Visible]: isVisible,
                [s.IsNegativeLabel]: v.value < 0,
              });

              return (
                <div key={i}>
                  {layout === LAYOUT_FANCY && (
                    <div
                      className={s.ValueWrap}
                      ref={(el) => {
                        valueLabelRefs.current[i] = {
                          element: el,
                        };
                      }}>
                      <div className={innerValueLabelWrapCls}>
                        <span
                          className={s.LabelValue}
                          aria-labelledby={`bar-chart-3d-label-${i}`}>
                          {v.valueLabel}
                        </span>
                      </div>
                    </div>
                  )}
                  <div
                    className={s.LabelWrap}
                    ref={(el) => {
                      labelRefs.current[i] = {
                        element: el,
                      };
                    }}>
                    <div className={innerLabelWrapCls}>
                      {layout !== LAYOUT_FANCY && (
                        <span
                          className={s.LabelValue}
                          aria-labelledby={`bar-chart-3d-label-${i}`}>
                          {v.valueLabel}
                        </span>
                      )}
                      <span className={s.Label} id={`bar-chart-3d-label-${i}`}>
                        {v.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

BarChart3d.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  infoTitle: PropTypes.string,
  infoText: PropTypes.string,
  infoLink: PropTypes.object,
  textTheme: PropTypes.oneOf(['light', 'dark']),
  theme: PropTypes.oneOf(['', THEME_ORANGE_PINK, THEME_BLUE_PURPLE]),
  layout: PropTypes.oneOf(['', LAYOUT_SIMPLE, LAYOUT_FANCY]),
  envMapUrl: PropTypes.string.isRequired,
  chartPadding: PropTypes.number,
  minBarHeight: PropTypes.number,
  maxBarHeight: PropTypes.number,
  minHorizontalWidth: PropTypes.number,
  plotData: PropTypes.shape({
    yAxisMax: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        valueLabel: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }).isRequired
    ),
  }).isRequired,
};

BarChart3d.defaultProps = {
  title: '',
  description: '',
  theme: THEME_ORANGE_PINK,
  textTheme: 'light',
  layout: LAYOUT_FANCY,
  envMapUrl: DEFAULT_ENV_MAP_URL,
  chartPadding: DEFAULT_CHART_PADDING,
  minBarHeight: MIN_BAR_HEIGHT,
  maxBarHeight: MAX_BAR_HEIGHT,
  plotData: null,
};

export default BarChart3d;
